import type { Task, TaskStatus } from '../../services/taskService';

export interface TaskStatusUpdate {
  task_id: string;
  status: TaskStatus;
  progress?: number;
  step_name?: string;
}

type TaskPatch = Partial<Task> & Pick<Task, 'id'>;

const taskPatchChangesValue = (current: Task, incoming: TaskPatch): boolean => (
  (Object.keys(incoming) as (keyof Task)[]).some((key) => (
    !Object.is(current[key], incoming[key])
  ))
);

/**
 * Merge partial task batches while preserving references for tasks that did
 * not change. Task SSE payloads are intentionally partial for most mutations.
 */
export function mergeTaskBatch(current: Task[], incoming: TaskPatch[]): Task[] {
  if (incoming.length === 0) return current;

  const byId = new Map(current.map((task) => [task.id, task]));
  let changed = false;

  // eslint-disable-next-line max-statements
  incoming.forEach((task) => {
    if (task.retired_planning_artifact) {
      if (byId.delete(task.id)) changed = true;

      return;
    }

    const existing = byId.get(task.id);

    if (!existing) {
      byId.set(task.id, task as Task);
      changed = true;

      return;
    }
    if (!taskPatchChangesValue(existing, task)) return;
    byId.set(task.id, { ...existing, ...task });
    changed = true;
  });

  return changed ? Array.from(byId.values()) : current;
}

export function applyTaskStatusUpdate(
  current: Task[],
  update: TaskStatusUpdate,
): { tasks: Task[]; found: boolean; needsSnapshotRefresh: boolean } {
  let found = false;
  let changed = false;
  let needsSnapshotRefresh = false;
  // eslint-disable-next-line max-statements
  const tasks = current.map((task) => {
    if (task.id !== update.task_id) return task;
    found = true;
    // Completion persists planning artifact receipts and workspace execution
    // evidence, including verified stage deployment. Compact events omit both.
    needsSnapshotRefresh = update.status === 'done';

    const status = task.status === 'ai_in_progress' && update.status === 'queued'
      ? task.status
      : update.status;
    const statusChanged = task.status !== status;
    const progressChanged = update.progress !== undefined
      && task.execution_progress !== update.progress;
    const stepChanged = update.step_name !== undefined
      && task.execution_step !== update.step_name;

    if (!statusChanged && !progressChanged && !stepChanged) return task;
    changed = true;

    return {
      ...task,
      status,
      ...(update.progress !== undefined ? { execution_progress: update.progress } : {}),
      ...(update.step_name !== undefined ? { execution_step: update.step_name } : {}),
    };
  });

  return { tasks: changed ? tasks : current, found, needsSnapshotRefresh };
}
